import { FormikErrors } from "formik";
import _ from "lodash";
import { isAddressBlank } from "src/components/Inputs/Address/Book/helpers";
import { VisibilityContext } from "src/components/Providers/FormProvider";
import { AddressValidationSchema, BaseAddress } from "src/schemas/Address";
import {
  CustomQuestionAnswer,
  CustomQuestionAnswerSchema,
} from "src/schemas/CustomQuestionType";
import * as AF from "../types/formTemplate";
import { Answer, Question } from "./formTemplate";
import { answerIsEmpty } from "./formTemplate/answer";
import { SelectedSchoolIds } from "./formTemplate/generalSection/question";
import {
  isValidEmail,
  isValidUsPhoneNumber,
  validateWithZod,
} from "./formValidations";
import {
  ARCHIVED_MULTI_SELECT_OPTION_ERROR,
  ARCHIVED_SINGLE_SELECT_OPTION_ERROR,
  EMAIL_TYPE_VALIDATION_ERROR,
  PHONE_NUMBER_TYPE_VALIDATION_ERROR,
  REQUIRED_FIELD_VALIDATION_ERROR,
} from "./url/constants";

const isAnswerValidEmail = (
  answers: Answer.FormikValues,
  questionId: string
): boolean => {
  const answer = answers[questionId];
  if (!answer) return true;

  if (typeof answer === "string") return isValidEmail(answer);
  return true;
};

const isAnswerValidUSPhoneNumber = (
  answers: Answer.FormikValues,
  questionId: string
): boolean => {
  const answer = answers[questionId];
  if (!answer) return true;
  if (typeof answer === "string") return isValidUsPhoneNumber(answer);
  return true;
};

/**
 * Validates a single question's answer and returns an error message if invalid.
 * This is a shared primitive used by both validateFormTemplate and VerificationDetails.
 *
 * IMPORTANT: This intentionally does NOT validate required fields.
 * Callers are responsible for checking required fields before calling this function.
 * - Main form (validateFormTemplate): Checks required FIRST, then calls this for type validation.
 * - Verification modal (createQuestionValidator): Checks required FIRST, then calls this.
 *
 * Also does NOT handle async validations (Address via Zod, CustomQuestion nested fields) -
 * those require special processing in validateFormTemplate.
 */
export function validateSingleQuestion(
  question: AF.Question<AF.WithId>,
  value: unknown
): string | undefined {
  // Skip validation for empty values (required validation is handled separately)
  if (value === undefined || value === null || value === "") {
    return undefined;
  }

  switch (question.type) {
    case AF.EmailType:
      if (typeof value === "string" && !isValidEmail(value)) {
        return EMAIL_TYPE_VALIDATION_ERROR;
      }
      break;

    case AF.PhoneNumberType:
      if (typeof value === "string" && !isValidUsPhoneNumber(value)) {
        return PHONE_NUMBER_TYPE_VALIDATION_ERROR;
      }
      break;

    case AF.DateType:
      {
        const dateError = Question.validateDate(
          value as string,
          question.constraints
        );
        if (dateError) return dateError;
      }
      break;

    case AF.NumberType:
      if (question.constraints) {
        const numberError = Question.validateNumber(
          value as number,
          question.constraints
        );
        if (numberError) return numberError;
      }
      break;

    case AF.SingleSelectType:
      if (typeof value === "string") {
        const selectedOption = question.options.find((o) => o.id === value);
        if (selectedOption?.isArchived) {
          return ARCHIVED_SINGLE_SELECT_OPTION_ERROR;
        }
      }
      break;

    case AF.MultiSelectType:
      if (Array.isArray(value)) {
        const hasArchivedOption = value.some((optionId) => {
          const selectedOption = question.options.find(
            (o) => o.id === optionId
          );
          return selectedOption?.isArchived;
        });
        if (hasArchivedOption) {
          return ARCHIVED_MULTI_SELECT_OPTION_ERROR;
        }
      }
      break;
  }

  return undefined;
}

export const validateRequiredQuestions = (
  questions: readonly AF.Question<AF.WithId>[],
  selectedSchoolIds: SelectedSchoolIds,
  answers: Answer.FormikValues,
  visibilityContext: VisibilityContext
) => {
  const completeQuestions = Question.getCompleteApplicableQuestions(
    questions,
    answers,
    selectedSchoolIds,
    visibilityContext
  );

  const requiredQuestions = Question.findRequiredQuestionIds(completeQuestions);

  const missingAnswerRequiredQuestions = [];

  for (const questionId of Object.keys(answers)) {
    if (
      requiredQuestions.includes(questionId) &&
      !Question.hasAnswer(answers, questionId)
    ) {
      missingAnswerRequiredQuestions.push(questionId);
      continue;
    }
  }

  return missingAnswerRequiredQuestions;
};

export const validateFailedConstraintsQuestions = (
  questions: readonly AF.Question<AF.WithId>[],
  selectedSchoolIds: SelectedSchoolIds,
  answers: Answer.FormikValues,
  visibilityContext: VisibilityContext
) => {
  const completeQuestions = Question.getCompleteApplicableQuestions(
    questions,
    answers,
    selectedSchoolIds,
    visibilityContext
  );
  return Question.findFailedConstraintsQuestionIds(completeQuestions, answers);
};

export const validateFormTemplate =
  (
    questions: readonly AF.Question<AF.WithId>[],
    selectedSchoolIds: SelectedSchoolIds,
    visibilityContext?: VisibilityContext
  ) =>
  async (
    answers: Answer.FormikValues
  ): Promise<FormikErrors<Answer.FormikValues>> => {
    const completeQuestions = Question.getCompleteApplicableQuestions(
      questions,
      answers,
      selectedSchoolIds,
      visibilityContext
    );

    const requiredQuestions =
      Question.findRequiredQuestionIds(completeQuestions);

    const addressQuestions = Question.findAddressQuestionIds(completeQuestions);

    const customQuestions = Question.findCustomQuestionIds(completeQuestions);

    const errors: FormikErrors<Answer.FormikValues> = {};

    // Validate each question using the shared primitive
    for (const question of completeQuestions) {
      const questionId = question.id;
      const answer = answers[questionId];

      // Check required fields first
      if (
        requiredQuestions.includes(questionId) &&
        !Question.hasAnswer(answers, questionId)
      ) {
        errors[questionId] = REQUIRED_FIELD_VALIDATION_ERROR;
        continue;
      }

      // Use shared validation primitive for type-specific validation
      // (email, phone, date constraints, number constraints, archived options)
      const validationError = validateSingleQuestion(question, answer);
      if (validationError) {
        errors[questionId] = validationError;
        continue;
      }

      // Handle async validations that require special processing
      if (addressQuestions.includes(questionId)) {
        const addressErrors = await validateAddressAnswer(answers, questionId);

        if (addressErrors !== undefined) {
          errors[questionId] = addressErrors as any;
        }

        continue;
      }

      if (customQuestions.includes(questionId)) {
        const customQuestion = completeQuestions.find(
          (q) => q.id === questionId
        ) as AF.CustomQuestion<AF.WithId>;
        const isRequired = requiredQuestions.includes(questionId);
        const customQuestionErrors = await validateCustomQuestionAnswer(
          customQuestion.nestedQuestions,
          answers,
          questionId,
          isRequired
        );

        if (customQuestionErrors !== undefined) {
          errors[questionId] = customQuestionErrors as any;
        }

        continue;
      }
    }
    return errors;
  };

async function validateAddressAnswer(
  answers: Answer.FormikValues,
  questionId: uuid
): Promise<FormikErrors<BaseAddress> | undefined> {
  const answer = answers[questionId];
  const validate = validateWithZod(AddressValidationSchema);

  if (!Answer.isAddressAnswer(answer)) {
    return await validate({
      street_address: "",
      street_address_line_2: "",
      city: "",
      state: "",
      zip_code: "",
    });
  }

  if (isAddressBlank(answer)) return undefined;

  return await validate(answer);
}

async function validateCustomQuestionAnswer(
  fieldQuestions: AF.ClonedQuestion<AF.WithId>[],
  answers: Answer.FormikValues,
  questionId: uuid,
  isRequired: boolean
): Promise<FormikErrors<CustomQuestionAnswer> | undefined> {
  const customQuestionAnswer = answers[questionId] as CustomQuestionAnswer;
  if (
    customQuestionAnswer === undefined ||
    (!isRequired && answerIsEmpty(customQuestionAnswer.answersByQuestionId))
  ) {
    return undefined;
  }

  const validate = validateWithZod(CustomQuestionAnswerSchema);
  const errors: FormikErrors<CustomQuestionAnswer> =
    (await validate(customQuestionAnswer)) ?? {};

  const fieldAnswers = customQuestionAnswer.answersByQuestionId;
  const fieldErrors: Record<string, string> = {};

  for (const fieldQuestion of fieldQuestions) {
    if (fieldQuestion.requirement && !fieldAnswers[fieldQuestion.id]) {
      fieldErrors[fieldQuestion.id] = REQUIRED_FIELD_VALIDATION_ERROR;
    }
    switch (fieldQuestion.type) {
      case AF.EmailType:
        if (!isAnswerValidEmail(fieldAnswers, fieldQuestion.id))
          fieldErrors[fieldQuestion.id] = EMAIL_TYPE_VALIDATION_ERROR;
        break;
      case AF.PhoneNumberType:
        if (!isAnswerValidUSPhoneNumber(fieldAnswers, fieldQuestion.id))
          fieldErrors[fieldQuestion.id] = PHONE_NUMBER_TYPE_VALIDATION_ERROR;
        break;
    }
  }

  if (!_.isEmpty(fieldErrors)) {
    errors.answersByQuestionId = fieldErrors;
  }

  return _.isEmpty(errors) ? undefined : errors;
}
